/**
 * AI Scribe — note shaping helpers
 *
 * The socket returns loosely-shaped section maps; the presentation layer wants a
 * flat, ordered list of labelled paragraphs. Keeping that translation here means
 * neither card component has to walk unknown objects.
 *
 * @author [Rajkumar Rathod]
 */

import {
  BARE_NUMBER_PATTERN,
  NOTE_METADATA_KEYS,
  SCRIBE_VITAL_UNITS,
} from "./ai-scriber-constant";
import { ScribeNoteData } from "./ai-scriber-types";

export interface NoteParagraph {
  /** Sub-field label — omitted when the section has a single paragraph. */
  label?: string;
  text: string;
}

export interface NoteSectionView {
  key: string;
  label: string;
  paragraphs: NoteParagraph[];
}

/** `followUpNote` / `FOLLOW_UP_NOTE` → `Follow Up Note`. */
export const humanizeNoteKey = (key: string): string =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/(?:^|\s)(\w)/g, (match) => match.toUpperCase());

const isFilledString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Appends the stored unit to a weight, height or temperature that has none. */
export const withVitalUnit = (key: string, value: string): string => {
  const unit = SCRIBE_VITAL_UNITS[key];
  if (!unit || !BARE_NUMBER_PATTERN.test(value.trim())) return value;
  return `${value.trim()} ${unit}`;
};

/** Nested maps (vitals, for example) collapse to one `key: value` line. */
const flattenNestedValues = (value: Record<string, unknown>): string =>
  Object.entries(value)
    .filter(([, nested]) => isFilledString(nested))
    .map(
      ([nestedKey, nested]) =>
        `${humanizeNoteKey(nestedKey)}: ${withVitalUnit(nestedKey, nested as string)}`
    )
    .join(" · ");

const toParagraphs = (content: unknown): NoteParagraph[] => {
  if (isFilledString(content)) return [{ text: content }];
  if (!isPlainObject(content)) return [];

  const paragraphs = Object.entries(content).reduce<NoteParagraph[]>(
    (acc, [key, value]) => {
      if (isFilledString(value)) {
        acc.push({ label: humanizeNoteKey(key), text: value });
        return acc;
      }

      if (Array.isArray(value)) {
        const text = value.filter(isFilledString).join(", ");
        if (text) acc.push({ label: humanizeNoteKey(key), text });
        return acc;
      }

      if (isPlainObject(value)) {
        const text = flattenNestedValues(value);
        if (text) acc.push({ label: humanizeNoteKey(key), text });
      }

      return acc;
    },
    []
  );

  /** A single paragraph reads as the section body itself — drop its sub-label. */
  return paragraphs.length === 1 ? [{ text: paragraphs[0].text }] : paragraphs;
};

/**
 * Flattens a generated note into renderable sections, dropping the metadata the
 * backend echoes back and any section that carries no text.
 */
export const toNoteSectionViews = (data?: ScribeNoteData): NoteSectionView[] =>
  Object.entries(data ?? {}).reduce<NoteSectionView[]>(
    (acc, [key, section]) => {
      if (NOTE_METADATA_KEYS.includes(key as never)) return acc;
      if (!isPlainObject(section)) return acc;

      const paragraphs = toParagraphs(section.content);
      if (paragraphs.length === 0) return acc;

      acc.push({
        key,
        label: humanizeNoteKey(
          isFilledString(section.title) ? section.title : key
        ),
        paragraphs,
      });

      return acc;
    },
    []
  );
